import GenericTable from 'modules/Tables'

import { IModel } from 'interfaces/model.interface'
import {
  INavigationPages,
  ISingleNavigationPage,
  defaultDashboardConfig,
} from 'interfaces/navigationPage.interface'

import { BioriskSlugs } from './BioriskMenu'
import {
  BioRiskMediaModel,
  BioRiskPressReleasesModel,
  BioriskDiseaseInformationModel,
  BioriskCandidateModel,
  BioriskApprovalsModel,
  BioriskMpoxCandidateModel,
} from './BioriskModels'
import BarChart from 'modules/Tables/Dashboard/AllDashboards/BarChart'
import { IDACandidatesDetailView } from 'routes/apps/ida360/Candidates'

type PandemicNavigationPage<TModel extends IModel<any>> = ISingleNavigationPage<
  BioriskSlugs,
  TModel
>

const VariantsMedia: PandemicNavigationPage<typeof BioRiskMediaModel> = {
  key: 'Media',
  model: {
    ...BioRiskMediaModel,
    excludeGeneratedCharts: true,
    hideTableHeader: true,
    name: 'News Reports',
  },
  path: ['news', 'media'],
  component: GenericTable,
  views: [
    {
      name: 'Default',
      airtableName: 'API Front End Export',
      airtableBase: 'idoutbreaks',
    },
  ],
}

const BioriskMpoxPipeline: PandemicNavigationPage<
  typeof BioriskCandidateModel
> = {
  key: 'MpoxPipeline',
  model: {
    ...BioriskCandidateModel,
    hideToolbar: true,
    tableFitHeight: true,
    quickFilter: 'candidateName',
    displayKey: 'candidateName',
    name: 'Mpox Candidates Competitive Landscape',
    newNote: {
      text: 'Click on Candidates for additional information on trials, approvals, news & press releases, revenue and supply.',
      newUntil: new Date(2025, 1, 1),
    },
  },
  component: GenericTable,
  path: null,
  views: [
    {
      name: 'Default',
      airtableName: 'outbreak_pipeline_mpox',
      airtableBase: 'lzdb',
    },
  ],
  autoGenerateDashboard: {
    ...defaultDashboardConfig,
    hideFilters: true,
    isEmbed: true,
    showSideCards: true,
    demoEnabled: false,
    render: BarChart,
    cardsRight: true,
    hideViewSelector: true,
    exportPageKey: 'MpoxPipelineTable',
    verticalAxisLabel: 'Number of Candidates',
    defaultStackByFilterValue: {
      label: 'Technology Type',
      value: 'type',
    },
    defaultHorizontalFilterValue: {
      label: 'Phase',
      value: 'latestPhaseManual',
    },
    sortingOrders: {
      latestPhaseManual: [
        'Preclinical',
        'Phase 0',
        'Phase I',
        'Phase I/II',
        'Phase II',
        'Phase II/III',
        'Phase III',
        'Approved',
        'Phase IV',
        'Paused',
        'Not Applicable',
        'Unknown',
      ],
    },
  },
}

const VariantsPressReleases: PandemicNavigationPage<
  typeof BioRiskPressReleasesModel
> = {
  key: 'PressReleases',
  model: {
    ...BioRiskPressReleasesModel,
    hideTableHeader: true,
    excludeGeneratedCharts: true,
  },
  component: GenericTable,
  path: ['news', 'press-releases'],
  views: [
    {
      name: 'Default',
      airtableName: 'API Front End Export',
      airtableBase: 'idoutbreaks',
    },
  ],
}

const DiseaseInformation: PandemicNavigationPage<
  typeof BioriskDiseaseInformationModel
> = {
  key: 'DiseaseInformation',
  model: {
    ...BioriskDiseaseInformationModel,
  },
  component: GenericTable,
  path: 'disease-information',
  views: [
    {
      name: 'Default',
      airtableName: 'disease_information',
      airtableBase: 'lzdb',
    },
  ],
}

const BioriskApprovals: PandemicNavigationPage<typeof BioriskApprovalsModel> = {
  key: 'Approvals',
  path: null,
  model: {
    ...BioriskApprovalsModel,
    excludeGeneratedCharts: true,
    hideViewSelector: true,
    quickFilter: 'candidateName',
    hideToolbar: true,
    fullHeader: true,
    hideTableHeader: true,
  },
  component: GenericTable,
  views: [
    {
      name: 'All',
      airtableName: 'outbreak_approval_mpox',
      airtableBase: 'lzdb',
    },
  ],
}

const BioriskMpoxPipelineTable: PandemicNavigationPage<
  typeof BioriskMpoxCandidateModel
> = {
  key: 'MpoxPipelineTable',
  model: {
    ...BioriskMpoxCandidateModel,
    excludeGeneratedCharts: true,
    hideViewSelector: true,
    detailViewType: 'Generic',
    renderDetailView: IDACandidatesDetailView,
    hideToolbar: true,
    fullHeader: true,
    hideTableHeader: true,
  },
  component: GenericTable,
  path: null,
  views: [
    {
      name: 'Default',
      airtableName: 'outbreak_pipeline_mpox',
      airtableBase: 'lzdb',
    },
  ],
}
export const BioriskPages: INavigationPages<BioriskSlugs> = {
  DiseaseInformation,
  VariantsMedia,
  VariantsPressReleases,
  BioriskMpoxPipeline,
  BioriskApprovals,
  BioriskMpoxPipelineTable,
}
